import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { ZoomIn } from 'lucide-react';
import { GALLERY } from '../../lib/constants';
import { trackEvent } from '../../lib/analytics';

const Gallery = () => {
  const [expanded, setExpanded] = useState(null);

  return (
    <section id="gallery" className="relative py-16 sm:py-20" data-testid="gallery-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div className="max-w-2xl">
            <span className="font-display uppercase tracking-[0.22em] text-[11px] text-[color:var(--neon-red)]">
              On The Mat
            </span>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl text-white tracking-tight">
              Real students. Real moments.
            </h2>
            <p className="mt-4 text-white/75 leading-relaxed">
              From stadium demonstrations to belt ceremonies — this is the Universal Kempo
              community in motion.
            </p>
          </div>
          <div className="font-mono-ui text-[11px] uppercase tracking-[0.18em] text-[color:var(--neon-cyan)] hidden sm:block">
            // gallery_v1 — colorado_springs
          </div>
        </div>

        {/* Asymmetric grid (12 cols on lg) */}
        <div
          className="mt-10 grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-12 auto-rows-[160px] sm:auto-rows-[200px] lg:auto-rows-[200px] gap-3 sm:gap-4"
          data-testid="gallery-grid"
        >
          {GALLERY.map((g, i) => (
            <motion.button
              key={g.id}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.5, delay: 0.05 * i }}
              onClick={() => { setExpanded(g); trackEvent('gallery_click', { id: g.id }); }}
              className={`group relative overflow-hidden rounded-2xl border border-white/10 hover:border-[color:var(--neon-red)]/50 transition-colors col-span-2 ${g.span || ''}`}
              style={{ boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.04)' }}
              data-testid={`gallery-item-${g.id}`}
            >
              <img
                src={g.url}
                alt={g.caption}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.06]"
                style={{ objectPosition: g.focus || 'center' }}
                loading="lazy"
              />
              {/* Tron overlay */}
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background:
                    'linear-gradient(to top, rgba(7,8,10,0.85) 0%, rgba(7,8,10,0.20) 45%, transparent 70%)',
                }}
              />
              {/* Red glow on hover */}
              <div
                className="absolute inset-0 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity"
                style={{ boxShadow: 'inset 0 0 0 1px rgba(255,42,42,0.55), inset 0 0 36px rgba(255,42,42,0.18)' }}
              />
              {/* Zoom indicator */}
              <div className="absolute top-3 right-3 h-8 w-8 grid place-items-center rounded-full bg-black/55 border border-white/20 opacity-0 group-hover:opacity-100 transition-opacity backdrop-blur-sm">
                <ZoomIn className="h-4 w-4 text-white" />
              </div>
              {/* Caption */}
              <div className="absolute left-4 right-4 bottom-3 text-left">
                <div className="font-mono-ui text-[10px] uppercase tracking-[0.18em] text-[color:var(--neon-cyan)]">
                  Universal Kempo
                </div>
                <div className="mt-0.5 font-display text-white text-sm sm:text-base tracking-tight drop-shadow-[0_2px_8px_rgba(0,0,0,0.65)]">
                  {g.caption}
                </div>
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      <Dialog open={!!expanded} onOpenChange={(o) => !o && setExpanded(null)}>
        <DialogContent
          className="bg-[#0B0D10] border border-[color:var(--neon-red)]/35 text-white max-w-4xl p-0 overflow-hidden"
          data-testid="gallery-lightbox"
          style={{ boxShadow: '0 0 0 1px rgba(255,42,42,0.35), 0 30px 80px rgba(0,0,0,0.6)' }}
        >
          {expanded && (
            <div className="relative">
              <img
                src={expanded.url}
                alt={expanded.caption}
                className="w-full h-auto max-h-[80vh] object-contain bg-black"
              />
              <div className="px-5 py-4 border-t border-white/10 flex items-center justify-between">
                <div>
                  <div className="font-mono-ui text-[10px] uppercase tracking-[0.18em] text-[color:var(--neon-cyan)]">
                    Universal Kempo — Colorado Springs
                  </div>
                  <div className="mt-1 font-display text-white text-base tracking-tight">
                    {expanded.caption}
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default Gallery;
